/**
 * Shared utilities used across the Files page cluster.
 * Pure functions only — no side-effects, no React imports.
 */
// React is used only in the type below; import it for the Dispatch type.
import type React from 'react';

/**
 * Returns a human-readable label for the given userType code.
 * Identical copy was present in CalculatedFilesPage, FinalFilesPage,
 * ProcessedFilesPage, and UploadedFilesPage.
 */
export const getUserTypeLabel = (userType: string): string => {
  if (userType === 'MA') return 'CarbonCare Admin';
  if (userType === 'LA') return 'Licensee Admin';
  return userType;
};

/**
 * Formats a byte count into a human-readable size string.
 * Identical copy was present in ProcessedFilesPage and UploadedFilesPage.
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Creates a sort-toggle handler for pages with a sortBy + sortDirection pair.
 * The logic was identical in ProcessedFilesPage and UploadedFilesPage.
 *
 * @param sortBy           Currently active sort column.
 * @param setSortBy        State setter for the sort column.
 * @param setSortDirection State setter for the sort direction.
 * @param setPage          State setter for the current page (reset to 0 on sort change).
 * @param defaultAscColumns Columns whose default direction when first selected is 'asc'.
 *                          All other columns default to 'desc'.  Pass ['fileName'] to
 *                          match the existing behaviour in all four pages.
 */
export function makeHandleSort<T extends string>(
  sortBy: T,
  setSortBy: (col: T) => void,
  setSortDirection: React.Dispatch<React.SetStateAction<'asc' | 'desc'>>,
  setPage: (page: number) => void,
  defaultAscColumns: T[] = ['fileName' as T],
): (column: string) => void {
  return (column: string) => {
    const col = column as T;
    if (sortBy === col) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDirection(defaultAscColumns.includes(col) ? 'asc' : 'desc');
    }
    setPage(0);
  };
}
